import { API_CONFIG } from '../config/api';

class AuthService {
  private tokenKey = 'colorfuel_jwt_token';

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  clearTokens(): void {
    localStorage.removeItem(this.tokenKey);
  }

  isTokenExpired(token?: string): boolean {
    const t = token || this.getToken();
    if (!t) return true;
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      return payload.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  }

  async login(username: string, password: string) {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH_LOGIN}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) throw new Error(`Login failed: ${response.status}`);
    
    const data = await response.json();
    if (data.success && data.token) {
      this.setToken(data.token);
      return data;
    }
    throw new Error('Invalid response');
  }

  async getValidToken(): Promise<string> {
    const token = this.getToken();
    if (!token) throw new Error('No token available');
    
    if (this.isTokenExpired(token)) {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH_REFRESH}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        }
      });
      
      if (!response.ok) {
        this.clearTokens();
        throw new Error('Token refresh failed');
      }
      
      const data = await response.json();
      this.setToken(data.token);
      return data.token;
    }
    
    return token;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && !this.isTokenExpired(token);
  }

  logout(): void {
    this.clearTokens();
  }
}

const authService = new AuthService();
export default authService;
